import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {Link} from '../../shared/link';
import {Pagination} from '../../shared/Pagination';
import {PageHeader} from '../../shared/PageHeader';
import {MakeApiCall, WriteConsoleErrors, ToString, ObjectToQueryString, ReplaceUrl} from '../../shared/utils';
import {FlagComponent} from '../AccountsListComponent/flag';
import {AccountComponent} from '../AccountsListComponent/account';
import {SimpleTooltip} from '../../shared/tooltip';
import {SimpleFilterItemComponent} from '../SimpleFilterItem';
import {INVOICE_STATE, UrlBuilder} from '../../app.defs';

import './styles.scss';

const INVOICES_ACTION = {
    NONE: 0,
    LOAD: 1
}

export class InvoicesListComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            isLoaded: false,
            accountFilter: props.accountFilter,
            invoiceFilter: props.invoiceFilter,
            invoiceTypeId: props.invoiceTypeId,
            pageNum: props.pageNum,
            pageSize: props.pageSize,
            action: INVOICES_ACTION.NONE,
            totalCount: 0,
            errors: null,
            invoices: [],
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderForm = this.renderForm.bind(this);
        this.renderTable = this.renderTable.bind(this);
        this.renderPagination = this.renderPagination.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const requestId = self.state.requestId + 1;

        const data = {
            accf: self.state.accountFilter,
            invf: self.state.invoiceFilter,
            invt: self.state.invoiceTypeId,
            pn: self.state.pageNum,
            ps: self.state.pageSize
        }

        new Promise(resolve => {
            self.setState({action: INVOICES_ACTION.LOAD, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                ReplaceUrl({
                    pathname: window.location.pathname,
                    search: ObjectToQueryString(data, true, {pn:1, ps: 50})
                });

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getInvoices',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: INVOICES_ACTION.NONE,
                    isLoaded: true,
                    accountFilter: response.accf,
                    invoiceFilter: response.invf,
                    invoiceTypeId: response.invt,
                    pageNum: response.pn,
                    pageSize: response.ps,
                    invoices: response.Invoices,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: INVOICES_ACTION.NONE, errors: errors})
            }
        });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.doLoad();
    }

    renderForm() {
        const self = this;
        const {action, accountFilter, invoiceFilter, invoiceTypeId} = self.state;
        const isDisabled = action != INVOICES_ACTION.NONE;

        const _renderFilterLink = (filter, negativeFilter, filterPattern) => {
            return (
                <SimpleFilterItemComponent
                    disabled={isDisabled}
                    filters={invoiceFilter}
                    filter={filter}
                    detectPattern={filterPattern}
                    negativeDetectPattern={negativeFilter}
                    onChange={f => self.setState({invoiceFilter: f})}
                />
            )
        }

        return (
            <div className="mb-n2 mt-2">
                <form className="form-inline" onSubmit={self.handleSubmit}>
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="InvoiceId, :filter"
                        style={{width: '400px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(invoiceFilter)}
                        onChange={e => self.setState({invoiceFilter: e.target.value})}
                    />
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="AccountId, Email, Name, Company"
                        style={{width: '400px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(accountFilter)}
                        onChange={e => self.setState({accountFilter: e.target.value})}
                    />
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={ToString(invoiceTypeId)}
                        onChange={e => self.setState({invoiceTypeId: e.target.value})}
                    >
                        {self.props.invoiceTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <button 
                        type="submit"
                        className="btn btn-outline-primary mb-2"
                        disabled={isDisabled}
                    >
                        <span className="fa fa-search mr-1" />
                        Search
                        <ButtonBusyIndicatorComponent visible={action == INVOICES_ACTION.LOAD} />
                    </button>
                </form>
                <div className="small">
                    Filters:&nbsp;
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':unp')} — unpaid;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':over')} — overdue;</span>
                </div>
            </div>
        )
    }

    renderTable() {
        const self = this;
        const {action, invoices} = self.state;
        const {timeZoneId} = self.props;
        
        const tzNow = moment.utc().tz(timeZoneId);
        const tzName = tzNow.format('z');
        const existsNew = invoices.some(p => tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day'));

        return (
            <table className="invoices table-striped-even table-hover table-responsive newstyle">
                <thead>
                    <tr>
                        {existsNew && <th width="1%" />}
                        <th width="1%">Invoice#</th>
                        <th width="1%" className="text-nowrap">Created ({tzName})</th>
                        <th width="1%" className="text-nowrap text-right">Amount</th>
                        <th width="1%" className="text-nowrap">Paid ({tzName})</th>
                        <th width="35%">Account</th>
                        <th width="1%" className="text-center"><span className="fa fa-globe" /></th>
                        <th width="25%">Email</th>
                        <th width="30%">Company</th>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map(p => {
                        let rowClass = '';
                        let textClass = '';
                        switch (p.InvoiceStateId) {
                            case INVOICE_STATE.Pending:
                                rowClass = 'warning';
                                if (Boolean(p.PaymentDeadLine) && moment.utc().isAfter(moment.utc(p.PaymentDeadLine))) {
                                    textClass = ' text-danger';
                                } 
                                break;
                            case INVOICE_STATE.Refunded:
                                rowClass = 'danger';
                                break;
                        }

                        return (
                            <tr key={'invoice-' + p.Id} className={rowClass}>
                                {existsNew && 
                                    <td>
                                        {tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day') && <span className="fa fa-flash text-success" />}
                                    </td>
                                }
                                <td>
                                    <Link to={UrlBuilder.InvoiceInfo({InvoiceId: p.Id})}>{p.Id}</Link>
                                </td>
                                <td className="text-nowrap">{moment.utc(p.CreatedAt).tz(timeZoneId).format('L LT')}</td>
                                <td className="text-right font-weight-bold text-nowrap">
                                    {(p.AffiliateCommission != 0) && !Boolean(p.AffiliateInfo) && <sup className="mre-1">{numeral(p.AffiliateCommission).USD()}</sup>}
                                    {(p.AffiliateCommission != 0) && Boolean(p.AffiliateInfo) &&
                                        <SimpleTooltip
                                            placement="right"
                                            title={`Commission to ${p.AffiliateInfo.Name}`}
                                        >
                                            <sup className="mre-1">
                                                <Link to={p.AffiliateInfo.AdminUrl} target="_blank">{numeral(p.AffiliateCommission).USD()}</Link>
                                            </sup>
                                        </SimpleTooltip>
                                    }
                                    {numeral(p.Amount).USD()}
                                </td>
                                <td className={'text-nowrap' + textClass}>
                                    {(p.InvoiceStateId == INVOICE_STATE.Paid) && moment.utc(p.PaidAt).tz(timeZoneId).format('L LT')}
                                    {(p.InvoiceStateId == INVOICE_STATE.Pending) && Boolean(p.PaymentDeadLine) && `deadline: ${moment.utc(p.PaymentDeadLine).tz(timeZoneId).format('L LT')}`}
                                </td>
                                <td>
                                    <AccountComponent 
                                        id={p.Account.Id}
                                        name={p.Account.Name}
                                        showId
                                        showLoginAs
                                        linkToInfo
                                    />
                                </td>
                                <td> 
                                    <FlagComponent
                                        countryId={p.Account.CountryId}
                                        countryName={p.Account.CountryName}
                                        lastIP={p.Account.LastIP}
                                    />
                                </td>
                                <td>{p.Account.Email}</td>
                                <td>{p.Account.Company}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    renderPagination() {
        const self = this;
        const {pageNum, pageSize, totalCount}  = self.state;

        const pagesCount = Math.ceil(totalCount / Math.max(1, pageSize));

        if (pagesCount <= 1) {
            return null;
        }

        return (
            <Pagination
                className="justify-content-end"
                pagesCount={pagesCount}
                pageNum={pageNum}
                maxButtons={7}
                onPageSelect={(pn) => self.setState({pageNum: pn}, self.doLoad)}
                size="sm"
            />
        )
    }

    render() {
        const self = this;
        const {totalCount, isLoaded, errors} = self.state;

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={null}
                    caption={
                        <h2 className="text-truncate">Invoices</h2>
                    }
                >
                    {self.renderForm()}
                </PageHeader>
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && <div className="small">{`${totalCount} invoice(s) found`}</div>}
                {self.renderTable()}
                <div className="pt-3">
                    {self.renderPagination()}
                </div>
           </div>
        )
    }
}
